import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthFailures, AuthFailureEntry } from '../../api/services/authFailuresService';
import { StatusBadge } from '../../components/AdminTable';

/**
 * Échecs de connexion Google / Apple, remontés par l'app (table AuthFailure).
 *
 * Quand Google refuse de délivrer un jeton, l'app affiche « Token Google
 * manquant » sans jamais appeler l'API : la cause n'existe que dans le
 * logcat du téléphone. L'app la classe et l'envoie ici, avec l'empreinte
 * SHA-1 qui signe l'APK installée — à comparer aux empreintes déclarées dans
 * Firebase (projet dingo-e0165, app io.angicia.dingo).
 */

const LIMIT = 200;

const REASONS: Record<string, { label: string; hint: string; tone: 'ok' | 'warn' | 'bad' | 'neutral' }> = {
  developer_error: {
    label: 'Configuration Google',
    hint: 'Google refuse cette app : l\'empreinte SHA-1 ci-dessous n\'est pas déclarée dans Firebase.',
    tone: 'bad'
  },
  no_credentials: { label: 'Aucun compte Google', hint: 'Aucun compte Google utilisable sur le téléphone.', tone: 'warn' },
  no_id_token: {
    label: 'Google sans jeton',
    hint: 'Le compte a été choisi mais Google n\'a pas rendu de jeton : souvent la même cause que la configuration.',
    tone: 'bad'
  },
  cancelled: { label: 'Annulé', hint: 'Fenêtre Google fermée. Suspect s\'il se répète pour un même appareil.', tone: 'neutral' },
  network: { label: 'Réseau', hint: 'Pas de connexion pendant l\'échange avec Google.', tone: 'warn' },
  internal_error: { label: 'Erreur interne Google', hint: 'Services Google Play en défaut sur le téléphone.', tone: 'warn' },
  sign_in_failed: { label: 'Connexion refusée', hint: 'Google a refusé la connexion (code 12500).', tone: 'bad' },
  invalid_token_response: { label: 'Jeton illisible', hint: 'Réponse de Google impossible à lire.', tone: 'bad' },
  no_output: { label: 'Cause non capturée', hint: 'iOS, ou aucune trace de la bibliothèque.', tone: 'neutral' },
  api_error: { label: 'Erreur API', hint: 'Google a répondu, c\'est l\'API qui a refusé : voir le message.', tone: 'bad' },
  register_error: { label: 'Erreur à l\'inscription', hint: 'La création du compte a échoué : voir le message.', tone: 'bad' },
  unknown: { label: 'Inconnue', hint: 'Voir le détail brut.', tone: 'neutral' }
};

const reasonOf = (reason: string) =>
  REASONS[reason] ?? { label: reason, hint: 'Voir le détail brut.', tone: 'neutral' as const };

const STAGES: Record<string, string> = {
  sign_in: 'Chez Google / Apple',
  api: 'API',
  register: 'Création du compte'
};

const formatDateTime = (dateString?: string) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString('fr-FR', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
};

/** « Dingo/1.3.9 (Android 14; Xiaomi 23053RN02Y) Ktor/3.0.3 » → « Android 14 · Xiaomi 23053RN02Y · 1.3.9 ». */
const deviceOf = (userAgent: string | null): string => {
  if (!userAgent) return '—';
  const match = userAgent.match(/Dingo\/([^\s]+)\s*\(([^)]*)\)/);
  if (!match) return userAgent;
  return [...match[2].split(';').map((s) => s.trim()).filter(Boolean), match[1]].join(' · ');
};

const ManagementAuthFailures = () => {
  const { token } = useAuth();
  const [rows, setRows] = useState<AuthFailureEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [provider, setProvider] = useState('');
  const [reason, setReason] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    getAuthFailures(token, { provider: provider || undefined, reason: reason || undefined, limit: LIMIT })
      .then(setRows)
      .catch((err: any) => setError(err.message || 'Erreur lors du chargement des échecs de connexion'))
      .finally(() => setLoading(false));
  }, [token, provider, reason]);

  useEffect(() => { load(); }, [load]);

  // Répartition par cause et empreintes vues, sur les lignes chargées : c'est
  // ce qui dit d'un coup d'œil s'il s'agit d'un problème de configuration.
  const byReason = useMemo(() => {
    const counts = new Map<string, number>();
    rows.forEach((r) => counts.set(r.reason, (counts.get(r.reason) ?? 0) + 1));
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  const fingerprints = useMemo(() => {
    const counts = new Map<string, number>();
    rows.forEach((r) => { if (r.signing_sha1) counts.set(r.signing_sha1, (counts.get(r.signing_sha1) ?? 0) + 1); });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-[0_1px_2px_rgb(19_21_26/0.04)]">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-[18px] font-semibold text-gray-900">Échecs de connexion ({rows.length.toLocaleString('fr-FR')})</h2>
            <p className="text-[12.5px] text-gray-500 mt-0.5">
              Connexions Google et Apple qui n'ont pas abouti, signalées par l'app — y compris celles qui n'ont jamais atteint l'API.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="">Google et Apple</option>
              <option value="google">Google</option>
              <option value="apple">Apple</option>
            </select>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="">Toutes les causes</option>
              {Object.entries(REASONS).map(([key, r]) => <option key={key} value={key}>{r.label}</option>)}
            </select>
            <button
              type="button"
              onClick={load}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50"
            >
              Actualiser
            </button>
          </div>
        </div>

        {!loading && rows.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
            <div className="border border-gray-100 rounded-xl p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Par cause</div>
              <ul className="space-y-1.5">
                {byReason.map(([key, count]) => (
                  <li key={key} className="flex items-center justify-between gap-3 text-sm">
                    <span className="min-w-0 truncate text-gray-800">{reasonOf(key).label}</span>
                    <span className="shrink-0 font-mono text-gray-500">{count}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border border-gray-100 rounded-xl p-3 min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Empreintes SHA-1 des APK en échec</div>
              {fingerprints.length === 0 ? (
                <p className="text-sm text-gray-500">Aucune (iOS, ou version de l'app antérieure au signalement).</p>
              ) : (
                <ul className="space-y-1.5">
                  {fingerprints.map(([sha, count]) => (
                    <li key={sha} className="flex items-center justify-between gap-3 text-sm">
                      <span className="min-w-0 font-mono text-xs text-gray-800 break-all">{sha}</span>
                      <span className="shrink-0 font-mono text-gray-500">{count}</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Chacune doit figurer dans Firebase → Paramètres du projet → app Android io.angicia.dingo.
              </p>
            </div>
          </div>
        )}

        {error && <div className="mb-4 text-sm text-red-600">{error}</div>}

        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-10 animate-pulse rounded-xl bg-gray-100" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-gray-500 text-sm">Aucun échec de connexion signalé pour ces critères</div>
        ) : (
          <ul className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
            {rows.map((row) => {
              const expanded = expandedId === row.id;
              const r = reasonOf(row.reason);
              return (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : row.id)}
                    aria-expanded={expanded}
                    className={`w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-gray-50 ${expanded ? 'bg-gray-50' : ''}`}
                  >
                    <span className="shrink-0 w-16 text-xs font-semibold text-gray-500 capitalize">{row.provider}</span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <StatusBadge status={r.label} tone={r.tone} />
                        <span className="hidden sm:inline text-xs text-gray-500">{STAGES[row.stage] ?? row.stage}</span>
                      </span>
                      <span className="block text-xs text-gray-500 truncate mt-0.5">{deviceOf(row.user_agent)}</span>
                    </span>
                    <span className="shrink-0 text-xs text-gray-500 w-32 text-right">{formatDateTime(row.created_at)}</span>
                  </button>
                  {expanded && (
                    <div className="px-3 pb-4 pt-1 bg-gray-50 space-y-3">
                      <p className="text-sm text-gray-700">{r.hint}</p>
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                        <div className="min-w-0"><dt className="inline text-gray-500">SHA-1 de l'APK : </dt><dd className="inline font-mono text-gray-800 break-all">{row.signing_sha1 || '—'}</dd></div>
                        <div><dt className="inline text-gray-500">Services Google Play : </dt><dd className="inline font-mono text-gray-800">{row.play_services || '—'}</dd></div>
                        <div><dt className="inline text-gray-500">Étape : </dt><dd className="inline text-gray-800">{STAGES[row.stage] ?? row.stage}</dd></div>
                        <div><dt className="inline text-gray-500">IP : </dt><dd className="inline font-mono text-gray-800">{row.ip || '—'}</dd></div>
                        <div className="sm:col-span-2 min-w-0"><dt className="inline text-gray-500">Client : </dt><dd className="inline text-gray-800 break-all">{row.user_agent || '—'}</dd></div>
                      </dl>
                      <div className="min-w-0">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Détail</div>
                        <pre className="text-xs bg-white border border-gray-100 rounded-xl p-3 overflow-auto max-h-72 whitespace-pre-wrap break-all text-gray-700">
                          {row.detail || '—'}
                        </pre>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ManagementAuthFailures;
